/**
 * Today's table, from the server (DLY-003).
 *
 * The browser never works out which day it is (D-027): it asks `/api/today`, and the
 * server answers by its own clock. Everything that comes back is untrusted input
 * (CLAUDE.md §10), so the level goes through `parseLevel`, and anything that does not
 * look exactly right is treated as an error rather than half-used.
 *
 * Four outcomes, and the page does something different for each:
 *   - `daily`        today's level: play it
 *   - `not-started`  before the first daily table: a labelled practice table (owner,
 *                    2026-09-25), and never after the first day, since only the server
 *                    can say this
 *   - `out-of-date`  the level needs newer physics than this page has: reload
 *   - `unavailable`  offline, server trouble, or a reply we cannot trust: retry
 */
import { levelIsPlayable, parseLevel, type Level } from '@potted/sim';

export const TODAY_URL = '/api/today';

export type TodaySource =
  | {
      readonly kind: 'daily';
      readonly level: Level;
      readonly serverNowMs: number;
      readonly nextRolloverMs: number;
      /** Where the archive starts, when the server says (DLY-005). */
      readonly firstDateKey?: string;
    }
  | { readonly kind: 'not-started'; readonly firstRolloverMs: number; readonly serverNowMs: number }
  | { readonly kind: 'out-of-date' }
  | { readonly kind: 'unavailable' };

// Shared by both readers: it fits both unions.
const UNAVAILABLE = { kind: 'unavailable' } as const;

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** A plausible epoch-milliseconds time: finite, and after 2020. Anything else is refused. */
function instant(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 1_577_836_800_000
    ? value
    : undefined;
}

/** Interpret a reply from `/api/today`. Pure, so every branch is tested without a network. */
export function readToday(status: number, body: unknown): TodaySource {
  const b = record(body);
  if (b === undefined) return UNAVAILABLE;

  if (status === 503 && b['error'] === 'not-started') {
    const firstRolloverMs = instant(b['firstRolloverMs']);
    const serverNowMs = instant(b['serverNowMs']);
    if (firstRolloverMs === undefined || serverNowMs === undefined) return UNAVAILABLE;
    return { kind: 'not-started', firstRolloverMs, serverNowMs };
  }

  if (status !== 200) return UNAVAILABLE;

  const serverNowMs = instant(b['serverNowMs']);
  const nextRolloverMs = instant(b['nextRolloverMs']);
  if (serverNowMs === undefined || nextRolloverMs === undefined) return UNAVAILABLE;
  if (typeof b['dateKey'] !== 'string') return UNAVAILABLE;

  let level: Level;
  try {
    level = parseLevel(b['level']);
  } catch {
    return UNAVAILABLE;
  }
  // The level must be the day the server says it is.
  if (level.dateKey !== b['dateKey']) return UNAVAILABLE;
  // A level solved under different physics would play differently here (N10).
  if (!levelIsPlayable(level)) return { kind: 'out-of-date' };

  const first = b['firstDateKey'];
  const firstDateKey = isDayKey(first) && first <= level.dateKey ? first : undefined;
  return {
    kind: 'daily',
    level,
    serverNowMs,
    nextRolloverMs,
    ...(firstDateKey === undefined ? {} : { firstDateKey }),
  };
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date written `YYYY-MM-DD`. For anything from the server or storage. */
export function isDayKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = DAY_KEY.exec(value);
  if (m === null) return false;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(ms).toISOString().slice(0, 10) === value;
}

/** A past day's table, for the archive (DLY-005), or why there is none. */
export type LevelSource =
  | { readonly kind: 'level'; readonly level: Level }
  | { readonly kind: 'out-of-date' }
  | { readonly kind: 'unavailable' };

/** Interpret a reply from `/api/level/:dateKey`, checked like today's. */
export function readLevelReply(dateKey: string, status: number, body: unknown): LevelSource {
  const b = record(body);
  if (status !== 200 || b === undefined || b['dateKey'] !== dateKey) return UNAVAILABLE;
  let level: Level;
  try {
    level = parseLevel(b['level']);
  } catch {
    return UNAVAILABLE;
  }
  if (level.dateKey !== dateKey) return UNAVAILABLE;
  if (!levelIsPlayable(level)) return { kind: 'out-of-date' };
  return { kind: 'level', level };
}

/** Ask the server for a past day. The server refuses any day after its own today. */
export async function fetchLevel(
  dateKey: string,
  fetchFn: typeof fetch = fetch,
): Promise<LevelSource> {
  if (!isDayKey(dateKey)) return UNAVAILABLE;
  let response: Response;
  try {
    response = await fetchFn(`/api/level/${dateKey}`, {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch {
    return UNAVAILABLE;
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return UNAVAILABLE;
  }
  return readLevelReply(dateKey, response.status, body);
}

/** Ask the server. Never throws: every failure is an outcome the page can show. */
export async function fetchToday(fetchFn: typeof fetch = fetch): Promise<TodaySource> {
  let response: Response;
  try {
    response = await fetchFn(TODAY_URL, {
      headers: { accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin',
    });
  } catch {
    return UNAVAILABLE;
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return UNAVAILABLE;
  }
  return readToday(response.status, body);
}
